/**
 * Objective Feed Service
 *
 * Fetches the objective-grouped feed from GET /objective-feed.
 * Maps the backend's snake_case response to the camelCase frontend types
 * defined in types/objectiveFeed.ts.
 */

import ApiService from './ApiService';
import type { FeedPost } from './FeedService';
import type {
  ObjectiveFeedResponse,
  ObjectiveFeedGroup,
  Objective,
  ObjectiveProgressDetail,
  ObjectiveProgressDetailBlock,
  ObjectiveAction,
  BetState,
} from '@customTypes/objectiveFeed';

// ---------------------------------------------------------------------------
// Raw API response types (snake_case, matching backend contract)
// ---------------------------------------------------------------------------

// Raw bet display block (snake_case) — only present on bet objectives.
interface RawBetDisplay {
  state?: string;
  hypothesis?: string;
  target?: {
    metric?: string;
    baseline?: string | number | null;
    threshold?: string | number | null;
  };
  kill_criteria?: string;
  levers?: string[];
  close_date?: string;
  must_decide_by?: string;
  start_at?: string | null;
  close_day_offset?: number | null;
  verdict?: string | null;
}

// RawObjective and mapObjective are shared with ObjectiveService
export interface RawObjective {
  id: string;
  title: string;
  description?: string;
  created_at: string;
  updated_at: string;
  kind?: string;
  bet?: RawBetDisplay;
}

export function mapObjective(raw: RawObjective): Objective {
  return {
    id: raw.id,
    title: raw.title,
    description: raw.description,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    // `kind` is absent for a normal goal; omit it rather than storing null so the
    // `objective.kind === 'bet'` checks downstream stay clean.
    ...(raw.kind ? { kind: raw.kind } : {}),
    ...(raw.bet
      ? {
          bet: {
            state: raw.bet.state as BetState | undefined,
            hypothesis: raw.bet.hypothesis,
            target: raw.bet.target,
            killCriteria: raw.bet.kill_criteria,
            levers: raw.bet.levers,
            closeDate: raw.bet.close_date,
            mustDecideBy: raw.bet.must_decide_by,
            startAt: raw.bet.start_at,
            closeDayOffset: raw.bet.close_day_offset,
            verdict: raw.bet.verdict,
          },
        }
      : {}),
  };
}

interface RawProgressDetail {
  summary?: string;
  blocks: Array<{
    type: 'text' | 'chart' | 'chart_xml';
    title?: string;
    body?: string;
    xml?: string;
  }>;
}

interface RawAction {
  action_id: string;
  title: string;
  description?: string | null;
  objective_id: string;
  status: 'pending' | 'completed' | 'dismissed';
  created_at: string;
}

interface RawObjectiveFeedGroup {
  objective: RawObjective;
  progress: number;
  progress_detail?: RawProgressDetail;
  actions?: RawAction[];
  posts: FeedPost[];
  archived_updates?: FeedPost[];
  last_activity_at?: string;
}

interface RawObjectiveFeedResponse {
  objectives: RawObjectiveFeedGroup[];
  next_cursor: string | null;
}

function mapProgressDetail(raw: RawProgressDetail): ObjectiveProgressDetail {
  return {
    summary: raw.summary,
    blocks: raw.blocks as ObjectiveProgressDetailBlock[],
  };
}

function mapAction(raw: RawAction): ObjectiveAction {
  return {
    actionId: raw.action_id,
    title: raw.title,
    description: raw.description,
    objectiveId: raw.objective_id,
    status: raw.status,
    createdAt: raw.created_at,
  };
}

function mapGroup(raw: RawObjectiveFeedGroup): ObjectiveFeedGroup {
  return {
    objective: mapObjective(raw.objective),
    progress: raw.progress,
    progressDetail: raw.progress_detail
      ? mapProgressDetail(raw.progress_detail)
      : undefined,
    actions: (raw.actions ?? []).map(mapAction),
    posts: raw.posts,
    archivedUpdates: raw.archived_updates,
    lastActivityAt: raw.last_activity_at,
  };
}

function mapResponse(raw: RawObjectiveFeedResponse): ObjectiveFeedResponse {
  return {
    objectives: raw.objectives.map(mapGroup),
    nextCursor: raw.next_cursor,
  };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class ObjectiveFeedService {
  /**
   * Fetch the objective-grouped feed for the current workspace.
   *
   * @param limit  – max objectives to return (default decided by backend)
   * @param cursor – pagination cursor from a previous response
   */
  public async getObjectiveFeed(
    limit?: number,
    cursor?: string,
  ): Promise<ObjectiveFeedResponse> {
    const params: Record<string, any> = {};
    if (limit) params.limit = limit;
    if (cursor) params.cursor = cursor;

    const { success, data } = await ApiService.get<RawObjectiveFeedResponse>(
      '/objective-feed',
      params,
    );

    if (success && data) {
      return mapResponse(data);
    }

    return { objectives: [], nextCursor: null };
  }
}

export default new ObjectiveFeedService();
